import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import TextareaOnboarding from "components/onboarding/TextareaOnboarding";
import KeyboardAwareBottomButton from "components/onboarding/KeyboardAwareBottomButton";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { userApi } from "api/userApi";
import { useOnboardingData } from "hooks/useOnboardingData";
import { onboardingApi, OnboardingDemographicType } from "api/onboardingApi";
import useIsInappWebview from "../../../../hooks/useIsInappWebview";
import { extractHexColor } from "../../../../styles/hexOpacity";
import { sendMessage } from "../../../../utils/webview";
import { useRecoilState } from "recoil";
import { userAtom } from "../../../../recoil/userAtom";

export default function Demographic_3() {
  const navigate = useNavigate();
  /* ➊ HTMLTextAreaElement 참조용 useRef 생성 */
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const isInappWebview = useIsInappWebview();
  const [, setUser] = useRecoilState(userAtom);

  const pageKey = "demographic_3";

  useEffect(() => {
    if (!isInappWebview) return;

    const topBackground = extractHexColor(theme.vars.palette.Background.Level1);
    const bottomBackground = extractHexColor(
      theme.vars.palette.Background.Level1
    );

    if (topBackground && bottomBackground) {
      sendMessage(
        JSON.stringify({
          event: "set_safearea_color",
          params: {
            topColor: `${topBackground}`,
            bottomColor: `${bottomBackground}`,
          },
        })
      );
    }
  }, [isInappWebview]);

  const { text: age, updateText: updateAge } = useOnboardingData(pageKey);

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const isButtonDisabled =
    age === undefined || age === "" || Number(age) <= 7 || Number(age) >= 100;

  const { mutate: submit } = useMutation(
    (age: number) => userApi.submitAge(age),
    {
      onSuccess: (_, age) => {
        if (age >= 25 && age <= 40) {
          setUser((prev) => (prev ? { ...prev, segmentType: "a" } : prev));
        }
        setLoading(false);
        navigate("/onboarding_new/demographic/4");
      },
    }
  );

  function handleSubmit() {
    if (age && age.length > 0 && Number(age) > 7) {
      setLoading(true);
      submit(Number(age));
    }
  }

  /* ➋ 페이지 로드 후 자동으로 텍스트에어리어에 포커스 */
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  // Session storage에서 데이터 복원
  useEffect(() => {
    const storedData = sessionStorage.getItem("demographic_data");
    if (storedData) {
      try {
        const demographicData: OnboardingDemographicType =
          JSON.parse(storedData);
        if (demographicData.age) {
          updateAge(demographicData.age.toString());
        }
      } catch (error) {
        console.error("Failed to parse demographic data:", error);
      }
    }
  }, []);

  const { mutate: savePage } = useMutation(
    (pageId: string) => onboardingApi.savePage(pageId),
    {
      onSuccess: () => {},
    }
  );

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    savePage(pageKey);
  });

  return (
    <Stack spacing={5} sx={{ height: "100%" }}>
      <Stack spacing={2}>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {
            "인생의 어느 시기를 지나고 있느냐에 따라 마음속 중요한 주제들이 변화하기도 한답니다."
          }
        </CustomTypography>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {"나이는 어떻게 되나요?"}
        </CustomTypography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        sx={{ width: "100%" }}
      >
        <Typography
          variant="Body"
          fontWeight={500}
          sx={{
            color: theme.vars.palette.Content.Strong,
          }}
        >
          만
        </Typography>
        <Box
          sx={{
            width: "100px",
            height: "54px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            boxShadow: isFocused
              ? `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`
              : `inset 0 0 0 0`,
          }}
        >
          <TextareaOnboarding
            initialValue={age}
            handleChange={(newValue: string) => {
              updateAge(newValue);

              // Session storage 업데이트
              const storedData = sessionStorage.getItem("demographic_data");
              const demographicData: OnboardingDemographicType = storedData
                ? JSON.parse(storedData)
                : {};
              demographicData.age = newValue ? Number(newValue) : undefined;
              sessionStorage.setItem(
                "demographic_data",
                JSON.stringify(demographicData)
              );
            }}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            emojiDisabled={true}
            readOnly={false}
            textAlign={"center"}
            numbersOnly={true}
            maxLength={2}
            /* ➌ 여기서 자식(TextareaOnboarding)에 ref를 넘김 */
            // inputRef={textAreaRef}
          />
        </Box>
        <Typography
          variant="Body"
          fontWeight={500}
          sx={{
            color: theme.vars.palette.Content.Strong,
          }}
        >
          세
        </Typography>
      </Stack>

      <KeyboardAwareBottomButton
        loading={loading}
        disabled={isButtonDisabled}
        onClick={handleSubmit}
        text={"다음"}
        highlightBackground={false}
      />
    </Stack>
  );
}
